import {
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSList,
  WSText,
  WSTooltipIcon
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { useDynamicFormEngineConfig } from "../../context/DynamicFormEngineConfigContext";
import { IFormNote, IFormSchemaField } from "../../types";
import { isSensitiveField } from "../../utils/isSharedPiiField";
import { DynamicFormNote } from "../DynamicFormNote";

type BoxProps = {
  field: IFormSchemaField;
  // Injected by WSForm.Field (RHF Controller). Falls back to the schema's
  // computed/prefill value for direct (non-form) rendering in tests.
  value?: unknown;
  /**
   * Plain (non-box) info notes of the computed field, rendered INSIDE the gray
   * box directly under the label row (e.g. "Using your date of birth …, we
   * calculated your age."). Boxed notes stay outside the box.
   */
  innerNotes?: IFormNote[];
};

const formatDisplay = (
  field: IFormSchemaField,
  value: unknown,
  currencyCode: string
): string => {
  if (field.type === "Currency")
    return formatMoney(
      Number(value ?? 0),
      // DynamicFormEngineConfig.currencyCode is an ISO 4217 string; formatMoney
      // accepts the template-literal form of its Currency union.
      currencyCode as Parameters<typeof formatMoney>[1]
    );
  if (value == null || value === "") return "—";
  return field.type === "Percent" ? `${value}%` : String(value);
};

// Pure read-only presentation for an auto-calculated value: a gray box with
// the field label + an "Auto-calculated" tag, any plain info notes, the value, and
// an info tooltip explaining the formula (sourced from the field description).
// Icon per the Figma: CURRENCY tiles carry a dollar glyph (dollar-circle — the
// library's only dollar icon; the design's plain "Dollar" doesn't exist in
// WSIcon); non-currency tiles (age, pay periods) render NO icon — the design's
// "Calculator" glyph doesn't exist in WSIcon either, and its non-currency
// tiles (e.g. "Your age") show none, so no icon is the faithful fallback.
// When an icon shows, the note and value rows are indented by the icon width
// + gap ("2XL" = 24px icon + 8px gap) so they left-align with the label TEXT.
export const AutoCalcBox: React.FC<BoxProps> = ({
  field,
  value,
  innerNotes = []
}) => {
  const { currencyCode } = useDynamicFormEngineConfig();
  const resolved =
    value ?? field.operations.computed?.value ?? field.prefill?.value;
  const tooltip = field.description ?? field.ui?.helpText;
  const hasIcon = field.type === "Currency";
  const rowIndent = hasIcon ? ("2XL" as const) : undefined;

  return (
    <WSElement colorBackground="gray50" p="M" mt="2XL">
      <WSList gap="XS">
        <WSFlexBox.CenterY gap="S" wrap="nowrap">
          {hasIcon ? (
            <WSIcon name="dollar-circle" size="L" color="gray500" />
          ) : null}
          <WSText weight="medium" color="gray700">
            {field.label}
          </WSText>
          <WSText.ParagraphXs color="gray500">Auto-calculated</WSText.ParagraphXs>
        </WSFlexBox.CenterY>
        {innerNotes.map((note, i) => (
          <WSElement key={`${field.key}-inner-note-${i}`} pl={rowIndent}>
            <DynamicFormNote note={note} size="S" />
          </WSElement>
        ))}
        <WSFlexBox.CenterY gap="XS" wrap="nowrap" pl={rowIndent}>
          {/* 14px / weight Book per the design — WSText.ParagraphSm (the plain
              WSText base kind is 16px; ParagraphSm is the 14/book style). */}
          <WSText.ParagraphSm fsExclude={isSensitiveField(field)}>
            {formatDisplay(field, resolved, currencyCode)}
          </WSText.ParagraphSm>
          {tooltip ? <WSTooltipIcon tooltip={tooltip} /> : null}
        </WSFlexBox.CenterY>
      </WSList>
    </WSElement>
  );
};

// Server-computed ("Auto-calculated") fields. Selected by DynamicFormFieldRenderer
// when the field carries `operations.computed`. Rendered THROUGH WSForm.Field so
// the field stays registered with RHF — its computed value (seeded into
// defaultValues and refreshed via setValue on re-evaluation) is then included in
// the submit payload and satisfies any validation rule, exactly as before this
// became a read-only display. RHF v6 defaults `shouldUnregister: true`, so an
// unregistered field would be dropped from the payload and fail required checks.
export const DynamicFieldAutoCalculated: React.FC<{
  field: IFormSchemaField;
  /** RHF path override — Group rows render item fields at `group[i].key`. */
  name?: string;
  /** Controller default for field-array rows (RHF v6 requires it there).
   * Typed `any` to fit WSForm.Field's DOM-derived defaultValue prop. */
  defaultValue?: any;
  innerNotes?: IFormNote[];
}> = ({ field, name, defaultValue, innerNotes }) => (
  <WSForm.Field
    name={name ?? field.key}
    defaultValue={defaultValue}
    component={AutoCalcBox}
    componentProps={{ field, innerNotes }}
  />
);
